/**
 * SimpleList — the slice of antd's <List> the console uses, rendered with plain
 * markup. antd 6.6 deprecates <List> (its replacement, <Listy>, is a virtual
 * list with no header/border/actions styling), so this keeps the antd 5 List
 * look: same paddings, split lines, bordered box, action strip and empty text,
 * all from the active theme tokens so dark mode follows.
 *
 * Supported props: dataSource, renderItem, rowKey, header, bordered,
 * size ('small' | default), loading, locale.emptyText.
 * Items: <SimpleList.Item actions={[...]} style onClick>.
 */
import React, { createContext, useContext } from 'react';
import { Empty, Spin, theme } from 'antd';

const ListCtx = createContext({ small: false, bordered: false, last: false, token: null });

function itemPadding(token, small, bordered) {
  if (small) {return `${token.paddingContentVerticalSM}px ${token.paddingContentHorizontal}px`;}
  if (bordered) {return `${token.paddingContentVertical}px ${token.paddingLG}px`;}
  return `${token.paddingContentVertical}px 0`;
}

function headerPadding(token, small, bordered) {
  if (bordered && small) {return `${token.paddingContentVerticalSM}px ${token.paddingContentHorizontal}px`;}
  if (bordered) {return `${token.paddingSM}px ${token.paddingLG}px`;}
  return `${token.paddingSM}px 0`;
}

function SimpleListItem({ actions, children, style, className, ...rest }) {
  const { small, bordered, last, token } = useContext(ListCtx);
  const split = `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`;
  const shown = (actions || []).filter(Boolean);
  return (
    <li
      className={['afm-list-item', className].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: itemPadding(token, small, bordered),
        color: token.colorText,
        borderBlockEnd: last ? 'none' : split,
        ...style,
      }}
      {...rest}
    >
      {children}
      {shown.length > 0 && (
        <ul style={{ flex: '0 0 auto', marginInlineStart: token.marginXXL, padding: 0, fontSize: 0, listStyle: 'none', margin: 0 }}>
          {shown.map((action, i) => (
            <li
              key={`action-${i}`}
              style={{
                position: 'relative',
                display: 'inline-block',
                padding: `0 ${token.paddingXS}px`,
                paddingInlineStart: i === 0 ? 0 : token.paddingXS,
                color: token.colorTextDescription,
                fontSize: token.fontSize,
                lineHeight: token.lineHeight,
                textAlign: 'center',
              }}
            >
              {action}
              {i !== shown.length - 1 && (
                <em style={{
                  position: 'absolute',
                  insetBlockStart: '50%',
                  insetInlineEnd: 0,
                  width: token.lineWidth,
                  height: token.fontHeight - token.marginXXS * 2,
                  transform: 'translateY(-50%)',
                  backgroundColor: token.colorSplit,
                }} />
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function itemKey(item, index, rowKey) {
  if (typeof rowKey === 'function') {return rowKey(item);}
  if (rowKey) {return item[rowKey];}
  return item && item.key !== undefined ? item.key : index;
}

export default function SimpleList({
  dataSource = [], renderItem, rowKey, header, bordered = false, size, loading = false, locale,
}) {
  const { token } = theme.useToken();
  const small = size === 'small';
  const split = `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`;

  let body;
  if (dataSource.length > 0) {
    body = (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {dataSource.map((item, index) => (
          <ListCtx.Provider
            key={itemKey(item, index, rowKey)}
            value={{ small, bordered, last: index === dataSource.length - 1, token }}
          >
            {renderItem(item, index)}
          </ListCtx.Provider>
        ))}
      </ul>
    );
  } else if (loading) {
    body = <div style={{ minHeight: 53 }} />;
  } else {
    body = (
      <div style={{ padding: token.padding, color: token.colorTextDisabled, fontSize: token.fontSize, textAlign: 'center' }}>
        {(locale && locale.emptyText) || <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </div>
    );
  }

  return (
    <div
      className="afm-list"
      style={{
        position: 'relative',
        color: token.colorText,
        fontSize: token.fontSize,
        lineHeight: token.lineHeight,
        ...(bordered
          ? { border: `${token.lineWidth}px ${token.lineType} ${token.colorBorder}`, borderRadius: token.borderRadiusLG }
          : {}),
      }}
    >
      {header && (
        <div style={{ padding: headerPadding(token, small, bordered), borderBlockEnd: split }}>{header}</div>
      )}
      <Spin spinning={loading}>
        <div style={loading ? { minHeight: token.controlHeight } : undefined}>{body}</div>
      </Spin>
    </div>
  );
}

SimpleList.Item = SimpleListItem;
